import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { BookingWidget } from "../components/booking/BookingWidget";
import { TrustStrip } from "../components/home/TrustStrip";
import { HowItWorks } from "../components/home/HowItWorks";
import { ReviewsCarousel } from "../components/home/ReviewsCarousel";
import { SelfServiceStrip } from "../components/home/SelfServiceStrip";
import { LifecycleStrip } from "../components/home/LifecycleStrip";
import { Reveal } from "../components/Reveal";
import api, { AED } from "../lib/api";
import { ShieldCheck, Clock, Car, Globe, Building2, ChevronRight, ArrowRight, BadgeDollarSign, BadgeCheck, Users, Baby, Truck, CreditCard, Plane, FileText } from "lucide-react";
import dayjs from "dayjs";

const HERO_IMG = "https://images.unsplash.com/photo-1611216373106-69aecc7e1f4f?crop=entropy&cs=srgb&fm=jpg&q=85";
const MONTHLY_IMG = "https://images.unsplash.com/photo-1569670380585-19bb0feec807?crop=entropy&cs=srgb&fm=jpg&q=85";

const JOURNEYS = [
  { tag: "Weekend escapes", title: "Chase the horizon", text: "Point the car at Hatta, Jebel Jais or the East Coast and just go. Unlimited UAE miles, roadside cover, and a 4x4 ready when you are.", img: "https://images.unsplash.com/photo-1762236095155-14181863cf3a?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { tag: "Every day, sorted", title: "The city, on your terms", text: "Skip the queues and surge fares. A car at your door, no deposit tying up your card, ready for the school run, the meeting, or a drive along the coast.", img: "https://images.unsplash.com/photo-1589390948789-416cb1c195f0?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { tag: "Long stays & new starts", title: "Feel at home, faster", text: "New to the Emirates? Go monthly for a lower rate with maintenance included, and the freedom to hand the keys back the moment your plans change.", img: "https://images.unsplash.com/photo-1690533681839-01c4d82d7c86?crop=entropy&cs=srgb&fm=jpg&q=85" },
];

const TRUST = [
  { icon: Clock, title: "24/7 Roadside", text: "Assistance anywhere in the UAE, any hour." },
  { icon: BadgeDollarSign, title: "No Hidden Charges", text: "Deposit, VAT and fees disclosed before you pay." },
  { icon: Car, title: "A Car for Every Journey", text: "A wide, well-kept choice of models, always a great fit for the trip ahead." },
  { icon: ShieldCheck, title: "Comprehensive Cover", text: "Third-party cover over 1 million USD, with CDW on every rental." },
];

const LADDER = [
  { label: "Daily", price: "AED 78", per: "per day incl. VAT", note: "1–6 days · full flexibility", to: "/rent-a-car" },
  { label: "Weekly", price: "AED 73", per: "per day incl. VAT", note: "7–21 days · weekly band rate", to: "/rent-a-car", featured: true },
  { label: "Monthly", price: "AED 1,537", per: "per month incl. VAT", note: "22+ days · maintenance included", to: "/monthly" },
];

const FAQS = [
  { q: "What is the difference between Short-Term and Monthly rentals?", a: "Short-term (daily and weekly) rentals cover 1–21 days with full flexibility. From 22 days, our monthly rentals cost less per day, include maintenance, and offer 1/3/6/9-month terms with savings on longer commitments." },
  { q: "Can I rent without a security deposit?", a: "Yes. Rent with No Deposit or Security blocking, available for a nominal fee based on vehicle group and tenure. Otherwise, a credit-card pre-authorization or debit-card deposit applies at handover." },
  { q: "How fast can you deliver a car to me?", a: "Deliveries run 09:00–21:00 daily. We need 3 hours notice in Dubai & Sharjah and 6 hours in Ajman, UAQ, RAK and Fujairah. Branch pickup is ready 3 hours after booking." },
  { q: "What documents do I need?", a: "UAE residents: Emirates ID and a UAE driving license. Tourists: passport, home license and an International Driving Permit where applicable. You upload them after booking, which takes about two minutes." },
];

const B2B = [
  ["Hotels & Hospitality", "hotels-hospitality"],
  ["Travel & Tourism", "travel-tourism"],
  ["Digital Aggregators", "digital-aggregators"],
  ["Corporate Mobility", "corporate-mobility"],
  ["Insurance & Roadside", "insurance-roadside"],
  ["Loyalty Programmes", "loyalty-programs"],
  ["Affiliate Sales", "affiliate-sales"],
  ["Connect With Us", "connect"],
];

const ADDONS = [
  { icon: ShieldCheck, name: "Super Collision Damage Waiver", price: "from AED 35/day", vat: "incl. VAT", text: "Bring your damage excess to zero and hand the keys back without a second thought, whatever the road threw at you.", tag: "Most added" },
  { icon: BadgeCheck, name: "No-Deposit Rental", price: "from AED 35/day", vat: "+ VAT", text: "Skip the AED 1,500+ card hold entirely. Your credit limit stays yours for the trip itself." },
  { icon: Users, name: "Additional Driver", price: "from AED 10/day", vat: "incl. VAT", text: "Share long drives legally and insured. Add a second driver in seconds at booking." },
  { icon: Baby, name: "Child & Baby Seats", price: "from AED 25/day", vat: "incl. VAT", text: "Safety-certified seats for every age, fitted and ready. Required by UAE law for under-4s." },
];

const WHY = [
  { icon: Car, title: "Our Fleet", text: "A wide choice of cars, from city runabouts to the Land Cruiser, ready for pickup or delivery across the Emirates.", to: "/fleet" },
  { icon: Globe, title: "Our Network", text: "14 branches across the UAE, with 24/7 desks at Dubai Terminals 1, 2 and 3 and Sharjah Airport, plus delivery in all six emirates.", to: "/network" },
  { icon: ShieldCheck, title: "The Europcar Promise", text: "Transparent pricing, a car that's ready when we say it is, and people who answer at 3am.", to: "/promise" },
];

export default function Home() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const initialMode = params.get("tab") === "monthly" || params.get("mode") === "monthly" ? "monthly" : "short_term";
  const [vehicles, setVehicles] = useState([]);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/blog").then((r) => setPosts(r.data.posts.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-[#fafcfa]">
      <Header />

      {/* Hero + booking widget */}
      <section className="relative">
        <div className="hero-clip relative h-[640px] sm:h-[560px]">
          <img src={HERO_IMG} alt="Driving through Dubai at golden hour" className="absolute inset-0 w-full h-full object-cover hero-zoom" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F1E17]/85 via-[#144733]/60 to-transparent" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-14 sm:pt-16">
            <p className="text-[#FFDC00] text-xs sm:text-sm uppercase tracking-[0.28em] font-semibold mb-5 fade-up">{t("Dubai & Northern Emirates")}</p>
            <h1 className="heading-upper text-4xl sm:text-5xl lg:text-6xl text-white max-w-3xl leading-[1.05] fade-up d1" data-testid="hero-heading">
              {t("Drive like you own it. Pay like you don't.")}
            </h1>
            <p className="text-white/85 text-base sm:text-lg mt-5 max-w-xl leading-relaxed fade-up d2">
              {t("Your keys to the Emirates. Short-term and monthly rentals with no deposit to tie up your card, delivered to your door in as little as 3 hours.")}
            </p>
          </div>
        </div>
        <div id="book" className="max-w-5xl mx-auto px-4 sm:px-6 -mt-[26rem] sm:-mt-80 relative z-10 pb-4">
          <BookingWidget initialMode={initialMode} />
        </div>
      </section>

      <TrustStrip />

      {/* Trust bar */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {TRUST.map((u, i) => (
          <div key={u.title} style={{ animationDelay: `${i * 0.09}s` }} className="bg-white rounded-2xl border border-[#E2E8F0] p-6 card-lift fade-up" data-testid="trust-card">
            <u.icon className="w-7 h-7 text-[#009900] mb-3" />
            <h3 className="font-bold text-[#0F1E17] mb-1">{t(u.title)}</h3>
            <p className="text-sm text-[#57685e] leading-relaxed">{t(u.text)}</p>
          </div>
        ))}
      </section>

      {/* Your journey — storytelling */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 w-full" data-testid="your-journey">
        <div className="promo-stripe mb-10">
          <p className="eyebrow mb-1">{t("Made for the moments that matter")}</p>
          <h2 className="heading-upper text-3xl sm:text-4xl text-[#144733] max-w-2xl leading-tight">{t("Your journey, your way")}</h2>
          <p className="text-sm text-[#57685e] mt-3 max-w-xl">{t("Whatever the Emirates has in store, a spontaneous escape, the daily run, or a long stay far from home, there's a Europcar that fits the moment.")}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {JOURNEYS.map((j, i) => (
            <Reveal key={j.title} delay={i * 120} className="group relative rounded-3xl overflow-hidden h-[420px] card-lift" data-testid={`journey-card-${i}`}>
              <img src={j.img} alt={t(j.title)} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F1E17]/92 via-[#0F1E17]/25 to-transparent" />
              <div className="absolute bottom-0 p-6">
                <p className="text-[#FFDC00] text-[11px] uppercase tracking-[0.2em] font-semibold mb-1.5">{t(j.tag)}</p>
                <h3 className="heading-upper text-xl text-white mb-1.5">{t(j.title)}</h3>
                <p className="text-white/85 text-sm leading-relaxed">{t(j.text)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* NEW: Tourist or Resident? Document quick-start */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-14 w-full" data-testid="doc-quickstart-section">
        <div className="promo-stripe mb-8">
          <p className="eyebrow mb-1">{t("Frictionless UAE onboarding")}</p>
          <h2 className="heading-upper text-2xl sm:text-3xl text-[#144733]">{t("What you need to rent in the UAE")}</h2>
          <p className="text-sm text-[#57685e] mt-1">{t("Clear documentation for visitors and residents alike, with no surprise paperwork at pickup.")}</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 card-lift" data-testid="tourist-doc-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5"><Plane className="w-6 h-6 text-[#009900]" /><h3 className="font-bold text-[#0F1E17] text-lg">{t("Tourists & visitors")}</h3></div>
              <span className="bg-[#FFDC00] text-[#0F1E17] text-[10px] uppercase tracking-wide font-bold px-2.5 py-1 rounded">{t("Airport pickup")}</span>
            </div>
            <ul className="space-y-2.5 text-sm text-[#57685e]">
              {["Passport with a valid UAE entry stamp or visa", "Home-country driving licence (GCC, UK, US, EU and many more)", "International Driving Permit where your licence isn't UAE-approved", "A payment card in the main driver's name"].map((d) => (
                <li key={d} className="flex gap-2.5"><BadgeCheck className="w-4 h-4 text-[#009900] shrink-0 mt-0.5" /><span>{t(d)}</span></li>
              ))}
            </ul>
            <Link to="/renters-guide" className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[#009900] hover:underline" data-testid="tourist-guide-btn">{t("Tourist guide & accepted licences")} <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 card-lift" data-testid="resident-doc-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5"><FileText className="w-6 h-6 text-[#009900]" /><h3 className="font-bold text-[#0F1E17] text-lg">{t("UAE residents")}</h3></div>
              <span className="bg-[#f0f5f0] text-[#144733] text-[10px] uppercase tracking-wide font-bold px-2.5 py-1 rounded border border-[#e2ebe4]">{t("Doorstep delivery")}</span>
            </div>
            <ul className="space-y-2.5 text-sm text-[#57685e]">
              {["Valid Emirates ID (original or digital)", "UAE driving licence held for at least 12 months (or GCC equivalent)", "A credit or debit card in the driver's name for the deposit", "Your delivery address across any of the six emirates"].map((d) => (
                <li key={d} className="flex gap-2.5"><BadgeCheck className="w-4 h-4 text-[#009900] shrink-0 mt-0.5" /><span>{t(d)}</span></li>
              ))}
            </ul>
            <Link to="/monthly" className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[#009900] hover:underline" data-testid="resident-deals-btn">{t("Explore resident monthly plans")} <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* NEW: Delivery across emirates + Salik/toll transparency */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-14 w-full" data-testid="delivery-salik-section">
        <div className="promo-stripe mb-8">
          <p className="eyebrow mb-1">{t("Transparent local operations")}</p>
          <h2 className="heading-upper text-2xl sm:text-3xl text-[#144733]">{t("Delivered across the emirates · Tolls made clear")}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 card-lift" data-testid="delivery-card-emirates">
            <Truck className="w-7 h-7 text-[#009900] mb-3" />
            <h3 className="font-bold text-[#0F1E17] mb-1">{t("Doorstep delivery & collection")}</h3>
            <p className="text-sm text-[#57685e] leading-relaxed">{t("To your hotel, villa, office or airport terminal across all six emirates, from 09:00 to 21:00, at AED 100 + VAT per trip, with free monthly delivery in Dubai and Sharjah.")}</p>
          </div>
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 card-lift" data-testid="salik-tracking-card">
            <CreditCard className="w-7 h-7 text-[#009900] mb-3" />
            <h3 className="font-bold text-[#0F1E17] mb-1">{t("Tolls, with no hidden markup")}</h3>
            <p className="text-sm text-[#57685e] leading-relaxed">{t("Every toll gate (Salik in Dubai, Darb in Abu Dhabi) is itemised and billed after your rental at the authority amount plus a clear 25% admin fee, all visible in your account.")}</p>
          </div>
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 card-lift" data-testid="insurance-mileage-card">
            <ShieldCheck className="w-7 h-7 text-[#009900] mb-3" />
            <h3 className="font-bold text-[#0F1E17] mb-1">{t("Insurance & mileage included")}</h3>
            <p className="text-sm text-[#57685e] leading-relaxed">{t("Comprehensive CDW, third-party cover over 1 million USD and unlimited UAE mileage on daily and weekly rentals, all included in the price you see.")}</p>
          </div>
        </div>
      </section>

      {/* No-deposit USP banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-14 w-full" data-testid="no-deposit-usp">
        <div className="rounded-2xl bg-[#144733] text-white p-8 sm:p-10 flex flex-col lg:flex-row lg:items-center gap-6 justify-between">
          <div className="border-l-[3px] border-[#FFDC00] pl-4">
            <p className="text-[#FFDC00] text-xs uppercase tracking-[0.22em] font-semibold mb-2">{t("Book with zero deposit")}</p>
            <h2 className="heading-upper text-2xl sm:text-3xl">{t("No deposit? No problem.")}</h2>
            <p className="text-white/85 text-sm mt-2 max-w-2xl leading-relaxed">{t("Skip the security hold entirely with our No-Deposit option. Add it for a small fee on any daily, weekly or monthly rental and nothing is blocked on your card. It's one of the easiest ways to drive away sooner.")}</p>
          </div>
          <Link to="/#book" className="btn-cta rounded px-6 py-3 text-sm whitespace-nowrap shrink-0" data-testid="no-deposit-cta">{t("Start your booking")}</Link>
        </div>
      </section>

      <HowItWorks />

      {/* Price ladder */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-14 w-full" data-testid="price-ladder">
        <div className="promo-stripe mb-8">
          <h2 className="heading-upper text-2xl sm:text-3xl text-[#144733]">{t("Simple, honest pricing")}</h2>
          <p className="text-sm text-[#57685e] mt-1">{t("All prices shown include 5% VAT. Rent with No Deposit or Security blocking, available for a nominal fee based on vehicle group and tenure.")}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {LADDER.map((l) => (
            <Link key={l.label} to={l.to}
              className={`rounded-2xl border p-6 card-lift flex flex-col ${l.featured ? "bg-[#144733] border-[#144733] text-white" : "bg-white border-[#E2E8F0]"}`}
              data-testid={`ladder-${l.label.toLowerCase()}`}>
              <span className={`text-xs uppercase tracking-widest font-semibold ${l.featured ? "text-[#FFDC00]" : "text-[#009900]"}`}>{t(l.label)}</span>
              <span className={`text-3xl font-bold mt-2 ${l.featured ? "text-white" : "text-[#0F1E17]"}`}>{t("from")} {t(l.price)}</span>
              <span className={`text-xs ${l.featured ? "text-white/70" : "text-[#57685e]"}`}>{t(l.per)}</span>
              <span className={`text-sm mt-3 ${l.featured ? "text-white/85" : "text-[#57685e]"}`}>{t(l.note)}</span>
              <span className={`mt-4 text-xs font-semibold uppercase tracking-wide flex items-center gap-1 ${l.featured ? "text-[#FFDC00]" : "text-[#009900]"}`}>
                {t("Choose")} {t(l.label)} <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Add-ons awareness strip */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-14 w-full" data-testid="addons-strip">
        <div className="promo-stripe mb-8">
          <h2 className="heading-upper text-2xl sm:text-3xl text-[#144733]">{t("Tailor your rental")}</h2>
          <p className="text-sm text-[#57685e] mt-1">{t("Every rental already includes CDW and third-party cover over 1 million USD. These popular extras take it further, add any of them in one tap at booking.")}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {ADDONS.map((a) => (
            <Link key={a.name} to="/#book" className="bg-white rounded-2xl border border-[#E2E8F0] p-6 card-lift flex flex-col relative" data-testid={`addon-card-${a.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              {a.tag && <span className="absolute top-4 right-4 bg-[#FFDC00] text-[#0F1E17] text-[9px] uppercase tracking-wide font-bold px-2 py-0.5 rounded">{t(a.tag)}</span>}
              <a.icon className="w-7 h-7 text-[#009900] mb-3" />
              <h3 className="font-bold text-[#0F1E17] text-sm">{t(a.name)}</h3>
              <span className="text-xs font-semibold text-[#009900] mt-0.5">{t(a.price)} {t(a.vat)}</span>
              <p className="text-xs text-[#57685e] leading-relaxed mt-2 flex-1">{t(a.text)}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Fleet strip */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-14 w-full">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div className="promo-stripe">
            <p className="eyebrow mb-1">{t("Popular on UAE roads")}</p>
            <h2 className="heading-upper text-2xl sm:text-3xl text-[#144733]">{t("Most-booked this month")}</h2>
            <p className="text-sm text-[#57685e] mt-1">{t("A great choice of cars across every category, immaculately kept and ready for pickup or delivery. You always get your chosen model or one very similar.")}</p>
          </div>
          <Link to="/fleet" className="text-xs font-semibold uppercase tracking-wide text-[#009900] flex items-center gap-1.5 hover:underline" data-testid="fleet-viewall">
            {t("Browse the full fleet")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {vehicles.slice(0, 6).map((v) => (
            <Link key={v.id} to="/fleet" className="group bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden card-lift" data-testid={`fleet-card-${v.id}`}>
              <div className="h-40 overflow-hidden bg-[#f4f7f4]">
                <img src={v.image} alt={v.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-4 flex justify-between items-center">
                <div>
                  <span className="text-[10px] uppercase tracking-widest text-[#57685e]">{v.category}</span>
                  <h3 className="font-bold text-[#0F1E17]">{v.name} <span className="text-xs font-normal text-[#57685e]">{t("or similar")}</span></h3>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-[#009900]">{AED(Math.round(v.daily_rate * 1.05))}</div>
                  <div className="text-[10px] text-[#57685e]">{t("per day incl. VAT")}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Monthly teaser */}
      <section className="bg-[#144733]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="promo-stripe">
              <h2 className="heading-upper text-2xl sm:text-3xl text-white">{t("Monthly Rental")}</h2>
              <p className="text-[#FFDC00] font-bold text-lg mt-2">{t("From AED 1,537/month incl. VAT · 22+ days")}</p>
            </div>
            <ul className="mt-6 space-y-3 text-white/85 text-sm">
              <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-[#7AB800] mt-0.5" /> {t("Terms of 1, 3, 6 or 9 months, saving up to 15% on longer terms")}</li>
              <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-[#7AB800] mt-0.5" /> {t("3,000 or 5,000 km per month · model year 2025 or 2026")}</li>
              <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-[#7AB800] mt-0.5" /> {t("Maintenance, registration and 24/7 roadside included")}</li>
              <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-[#7AB800] mt-0.5" /> {t("Rent with No Deposit or Security blocking for a nominal fee")}</li>
            </ul>
            <Link to="/monthly" className="btn-cta rounded px-6 py-3 text-sm inline-block mt-8" data-testid="monthly-promo-cta">
              {t("Explore monthly plans")}
            </Link>
          </div>
          <div className="rounded-lg overflow-hidden h-72 hidden lg:block">
            <img src={MONTHLY_IMG} alt="SUV in the desert" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Why Europcar teaser */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 w-full grid md:grid-cols-3 gap-6" data-testid="why-europcar-teaser">
        {WHY.map((c) => (
          <Link key={c.title} to={c.to} className="bg-white rounded-2xl border border-[#E2E8F0] p-6 card-lift">
            <c.icon className="w-7 h-7 text-[#009900] mb-3" />
            <h3 className="font-bold text-[#0F1E17] mb-1">{t(c.title)}</h3>
            <p className="text-sm text-[#57685e] leading-relaxed">{t(c.text)}</p>
            <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-[#009900]">{t("Learn more")} <ArrowRight className="w-3.5 h-3.5" /></span>
          </Link>
        ))}
      </section>

      {/* Top FAQs */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-14 w-full" data-testid="home-faqs">
        <div className="promo-stripe mb-8">
          <h2 className="heading-upper text-2xl sm:text-3xl text-[#144733]">{t("Good to know")}</h2>
        </div>
        <div className="space-y-3">
          {FAQS.map((f) => (
            <details key={f.q} className="bg-white border border-[#E2E8F0] rounded-xl p-4 group">
              <summary className="cursor-pointer text-sm font-semibold text-[#0F1E17] list-none flex justify-between items-center">
                {t(f.q)} <ChevronRight className="w-4 h-4 text-[#009900] group-open:rotate-90 transition-transform" />
              </summary>
              <p className="text-sm text-[#57685e] mt-3 leading-relaxed">{t(f.a)}</p>
            </details>
          ))}
        </div>
        <Link to="/help" className="inline-block mt-4 text-xs font-semibold uppercase tracking-wide text-[#009900] hover:underline">{t("All questions answered in Help & Support →")}</Link>
      </section>

      <LifecycleStrip />

      <ReviewsCarousel />

      <SelfServiceStrip />

      {/* Blog teaser */}
      {posts.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 w-full" data-testid="home-blog-section">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div className="promo-stripe">
              <h2 className="heading-upper text-2xl sm:text-3xl text-[#144733]">{t("From the journal")}</h2>
              <p className="text-sm text-[#57685e] mt-1">{t("UAE driving guides, rental wisdom and the case for flexibility.")}</p>
            </div>
            <Link to="/blog" className="text-xs font-semibold uppercase tracking-wide text-[#009900] flex items-center gap-1.5 hover:underline" data-testid="home-blog-viewall">
              {t("View all articles")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {posts.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="group bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden card-lift" data-testid="home-blog-card">
                <div className="h-44 overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <span className="text-[10px] uppercase tracking-widest font-semibold text-[#009900]">{p.category}</span>
                  <h3 className="font-bold text-[#0F1E17] mt-1.5 leading-snug group-hover:text-[#005125] transition-colors">{p.title}</h3>
                  <span className="text-[11px] text-[#8a978f] block mt-3">{p.reading_time} {t("min read")} · {dayjs(p.published).format("DD MMM YYYY")}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* B2B strip */}
      <section className="bg-[#f0f5f0] border-y border-[#e2ebe4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6 justify-between">
            <div className="promo-stripe">
              <h2 className="heading-upper text-xl sm:text-2xl text-[#144733] flex items-center gap-2"><Building2 className="w-6 h-6 text-[#009900]" /> {t("Partner with us")}</h2>
              <p className="text-sm text-[#57685e] mt-1">{t("Dedicated rates, volume booking and priority support for 8 partner categories.")}</p>
            </div>
            <div className="flex flex-col gap-4 max-w-xl">
              <div className="flex flex-wrap gap-2" data-testid="b2b-categories">
                {B2B.map(([label, slug]) => (
                  <Link key={slug} to={`/partner-with-us/${slug}`}
                    className="bg-white border border-[#d5ddd5] text-[#144733] text-xs font-medium px-3 py-1.5 rounded-full hover:border-[#009900] transition-colors">{t(label)}</Link>
                ))}
              </div>
              <Link to="/partner-with-us" className="btn-cta rounded px-6 py-3 text-xs w-fit" data-testid="b2b-partner-cta">{t("Partner with us")}</Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
